import { motion } from 'framer-motion';
import { Flag, Ruler, Trophy, Lightbulb, X } from 'lucide-react';
import { Button } from '../ui/button';

// Translations
const translations = {
  ca: {
    hole: 'Forat',
    par: 'Par',
    distance: 'Distància',
    handicap: 'Handicap',
    meters: 'm',
    yards: 'yd',
    viewDetails: 'Veure detalls',
    tips: 'Consells de joc',
    description: 'Descripció',
    close: 'Tancar'
  },
  es: {
    hole: 'Hoyo',
    par: 'Par',
    distance: 'Distancia',
    handicap: 'Handicap',
    meters: 'm',
    yards: 'yd',
    viewDetails: 'Ver detalles',
    tips: 'Consejos de juego',
    description: 'Descripción',
    close: 'Cerrar'
  },
  fr: {
    hole: 'Trou',
    par: 'Par',
    distance: 'Distance',
    handicap: 'Handicap',
    meters: 'm',
    yards: 'yd',
    viewDetails: 'Voir détails',
    tips: 'Conseils de jeu',
    description: 'Description',
    close: 'Fermer'
  },
  en: {
    hole: 'Hole',
    par: 'Par',
    distance: 'Distance',
    handicap: 'Handicap',
    meters: 'm',
    yards: 'yd',
    viewDetails: 'View details',
    tips: 'Playing tips',
    description: 'Description',
    close: 'Close'
  }
};

// Placeholder images for holes without custom images
const getPlaceholderImage = (holeNumber) => {
  const images = [
    'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?q=80&w=800',
    'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?q=80&w=800',
    'https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?q=80&w=800',
    'https://images.unsplash.com/photo-1592919505780-303950717480?q=80&w=800',
    'https://images.unsplash.com/photo-1580127411778-f8b6a7c6dde2?q=80&w=800',
    'https://images.unsplash.com/photo-1600005082646-dbdf4e78e3bb?q=80&w=800',
    'https://images.unsplash.com/photo-1632339855172-3a6942beb8a3?q=80&w=800',
    'https://images.unsplash.com/photo-1596727362302-b8d891c42ab8?q=80&w=800',
    'https://images.unsplash.com/photo-1565992441121-4367c2967103?q=80&w=800',
    'https://images.unsplash.com/photo-1611374243147-44a702c2d44c?q=80&w=800',
    'https://images.unsplash.com/photo-1595841696677-6489ff3f8cd1?q=80&w=800',
    'https://images.unsplash.com/photo-1591491719419-da54d5e8a5ec?q=80&w=800',
    'https://images.unsplash.com/photo-1535132011086-b8818f016104?q=80&w=800',
    'https://images.unsplash.com/photo-1609861832961-e5a15d01d6e3?q=80&w=800',
    'https://images.unsplash.com/photo-1622561167037-e6bc04b2a8d8?q=80&w=800',
    'https://images.unsplash.com/photo-1613112743258-3b7c4ed1f8ed?q=80&w=800',
    'https://images.unsplash.com/photo-1614117819244-c25e26f25cd7?q=80&w=800',
    'https://images.unsplash.com/photo-1547132809-51f62eb4a0b2?q=80&w=800'
  ];
  return images[(holeNumber - 1) % images.length];
};

// Par color based on difficulty
const getParColor = (par) => {
  switch (par) {
    case 3: return 'bg-green-500';
    case 4: return 'bg-blue-500';
    case 5: return 'bg-orange-500';
    default: return 'bg-gray-500';
  }
};

// HoleCard component - displayed in grid
export const HoleCard = ({ hole, language = 'ca', onClick }) => {
  const t = translations[language] || translations.ca;
  const image = hole.image || getPlaceholderImage(hole.number);

  return (
    <motion.div
      className="group bg-white border border-[#E5E5E5] rounded-lg overflow-hidden cursor-pointer hover:border-[#1A4D2E] transition-colors"
      whileHover={{ y: -4 }}
      onClick={onClick}
      data-testid={`hole-card-${hole.number}`}
    >
      {/* Image - 4:3 aspect ratio for hole maps (1200x900) */}
      <div className="relative aspect-[4/3] overflow-hidden bg-white">
        <img
          src={image}
          alt={`${t.hole} ${hole.number}`}
          className="w-full h-full object-contain bg-white transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>

      {/* Description only */}
      {hole.description && (
        <div className="p-4 border-t border-[#E5E5E5]">
          <p className="text-sm text-[#666666] line-clamp-3">
            {hole.description}
          </p>
        </div>
      )}
    </motion.div>
  );
};

// HoleDetail component - simple image popup
export const HoleDetail = ({ hole, language = 'ca', onClose }) => {
  const t = translations[language] || translations.ca;
  const image = hole.image || getPlaceholderImage(hole.number);

  return (
    <div 
      className="relative w-full max-w-4xl mx-auto" 
      data-testid={`hole-detail-${hole.number}`}
      onClick={(e) => e.stopPropagation()}
    >
      <img
        src={image}
        alt={`${t.hole} ${hole.number}`}
        className="w-full h-auto rounded-lg shadow-2xl"
      />
    </div>
  );
};

export default HoleCard;
